import { useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { LogIn, ShieldCheck, AlertTriangle, Building2, UserCog, KeyRound } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input, Label, FieldError, FieldHint } from '@/components/ui/Input'
import { CenterSpinner } from '@/components/ui/Spinner'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useLogin, useNeedsSetup, useSetupInitialAdmin } from '@/lib/api/hooks'
import { useToast } from '@/components/ui/ToastProvider'

const LAST_EMAIL_KEY = 'adaptivo.szamla.lastEmail'

export function LoginPage() {
  const { data: needsSetup, isLoading } = useNeedsSetup()

  if (isLoading) return <CenterSpinnerScreen />
  if (needsSetup) return <SetupWizard />
  return <LoginForm />
}

function CenterSpinnerScreen() {
  return (
    <div className="min-h-screen grid place-items-center">
      <CenterSpinner />
    </div>
  )
}

function Hero({ children }: { children?: React.ReactNode }) {
  return (
    <div className="hidden lg:flex flex-col justify-between text-white p-10 relative overflow-hidden cosmic-bg">
      {/* Hálózati pontháló dekoráció — a logó hátterét idéző "Föld-szövet" */}
      <div className="absolute inset-0 cosmic-grid opacity-40" aria-hidden />
      {/* Subtle radial mask hogy a grid középen ne legyen túl erős */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(10, 12, 20, 0.7) 0%, transparent 65%)',
        }}
      />

      {/* CSS-rotated orbital decoration — a logó orbital ring-jét echoing nagyban */}
      <div
        className="absolute -right-20 top-1/2 -translate-y-1/2 pointer-events-none orbit-rotate"
        aria-hidden
      >
        <svg width="560" height="560" viewBox="0 0 560 560" fill="none">
          <defs>
            <radialGradient id="hero-dot" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0%" stopColor="#67e8f9" stopOpacity="1" />
              <stop offset="60%" stopColor="#22d3ee" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#22d3ee" stopOpacity="0" />
            </radialGradient>
          </defs>
          <ellipse cx="280" cy="280" rx="240" ry="60" fill="none" stroke="#22d3ee" strokeWidth="1" opacity="0.35" transform="rotate(-22 280 280)" />
          <ellipse cx="280" cy="280" rx="200" ry="48" fill="none" stroke="#22d3ee" strokeWidth="0.6" opacity="0.22" transform="rotate(-22 280 280)" />
          <circle cx="500" cy="195" r="9" fill="url(#hero-dot)" />
          <circle cx="500" cy="195" r="2.5" fill="#ffffff" />
        </svg>
      </div>

      {/* Tartalom */}
      <div className="relative z-10 flex items-center gap-3">
        <img
          src="/adaptivo-mark.svg"
          alt=""
          width={56}
          height={56}
          className="h-14 w-14 drop-shadow-[0_0_24px_rgba(34,211,238,0.5)]"
        />
        <div>
          <div className="text-lg font-semibold tracking-tightish">Adaptivo Számlakezelő</div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-cyan-300/80">
            NAV integráció · AI-asszisztens
          </div>
        </div>
      </div>

      <div className="relative z-10 max-w-lg space-y-4">
        <h2 className="text-4xl font-semibold leading-[1.1] tracking-tightish">
          Beérkezett számla
          <br />
          <span className="bg-gradient-to-r from-cyan-300 via-emerald-300 to-amber-300 bg-clip-text text-transparent">
            → NAV-val párosítva →
          </span>
          <br />
          könyvelőnek átadva.
        </h2>
        <p className="text-slate-300 text-[15px] leading-relaxed">
          A pénzügyi kontroll modul Hungarian KKV-knak. NAV-szinkron, IMAP fogadás, AI kategorizálás,
          havi könyvelői csomag — minden audit-olt és emberi jóváhagyással.
        </p>
        {children ?? (
          <ul className="space-y-2 text-sm text-slate-300">
            <li className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-cyan-400" /> Tenant-szigorú izoláció
            </li>
            <li className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-cyan-400" /> Audit-olt művelet minden lépésben
            </li>
            <li className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-cyan-400" /> Emberi jóváhagyás minden kifizetés és könyvelőnek küldés előtt
            </li>
          </ul>
        )}
      </div>

      <div className="relative z-10 flex items-center justify-end text-xs">
        <span className="text-cyan-400/70 tracking-[0.18em] uppercase text-[10px]">
          Software solutions
        </span>
      </div>
    </div>
  )
}

function LoginForm() {
  const auth = useAuth()
  const nav = useNavigate()
  const loc = useLocation() as { state?: { from?: { pathname?: string } } }
  const loginMut = useLogin()
  const toast = useToast()
  const passwordRef = useRef<HTMLInputElement>(null)

  // Az utolsó használt email-t megőrizzük — gyakori login-flow esetén másoláshoz/közönséges UX-hez.
  const initialEmail = (() => {
    try {
      return window.localStorage.getItem(LAST_EMAIL_KEY) ?? ''
    } catch {
      return ''
    }
  })()

  const [email, setEmail] = useState(initialEmail)
  const [password, setPassword] = useState('')
  const [capsLock, setCapsLock] = useState(false)

  // Ha van pre-fill email, fókusz a jelszóra induljon (gyorsabb 2. belépés).
  useEffect(() => {
    if (initialEmail) {
      passwordRef.current?.focus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function detectCaps(e: React.KeyboardEvent<HTMLInputElement>) {
    setCapsLock(e.getModifierState && e.getModifierState('CapsLock'))
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    try {
      const session = await loginMut.mutateAsync({ email: email.trim(), password })
      try {
        window.localStorage.setItem(LAST_EMAIL_KEY, email.trim())
      } catch {
        // noop
      }
      auth.login(session)
      const dest = loc.state?.from?.pathname ?? '/invoices'
      nav(dest, { replace: true })
      toast.show({ tone: 'success', title: 'Sikeres belépés', description: session.user.fullName })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ismeretlen hiba'
      toast.show({ tone: 'error', title: 'Belépés sikertelen', description: message })
      // hibás belépés után jelszót töröljük, fókuszt visszaállítjuk
      setPassword('')
      passwordRef.current?.focus()
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50 dark:bg-slate-950">
      <Hero />
      <div className="flex items-center justify-center p-6 sm:p-10">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-5">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Belépés</h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {initialEmail ? (
                <>Korábbi bejelentkezés:{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setEmail('')
                      try {
                        window.localStorage.removeItem(LAST_EMAIL_KEY)
                      } catch {
                        // noop
                      }
                    }}
                    className="underline text-brand-600 hover:text-brand-700"
                  >
                    nem ez a fiók
                  </button>
                </>
              ) : (
                'Add meg a regisztrált email címedet és jelszavadat.'
              )}
            </p>
          </div>

          <div>
            <Label htmlFor="email">Email cím</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="nev@adaptivo.hu"
              required
              autoFocus={!initialEmail}
              inputMode="email"
              spellCheck={false}
            />
          </div>

          <div>
            <Label htmlFor="password">Jelszó</Label>
            <Input
              id="password"
              ref={passwordRef}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={detectCaps}
              onKeyUp={detectCaps}
              required
            />
            {capsLock && (
              <p className="mt-1 text-xs text-amber-700 dark:text-amber-300 flex items-center gap-1.5">
                <KeyRound className="h-3 w-3" /> Caps Lock be van kapcsolva
              </p>
            )}
          </div>

          <Button
            type="submit"
            fullWidth
            loading={loginMut.isPending}
            disabled={!email.trim() || !password}
            leftIcon={<LogIn className="h-4 w-4" />}
          >
            Belépés
          </Button>

          {loginMut.isError && <FieldError>{(loginMut.error as Error).message}</FieldError>}

          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/30 dark:border-amber-900/50 dark:text-amber-200 flex items-start gap-2">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span>
              Önregisztráció nincs. Új felhasználót a cégvezető hoz létre a{' '}
              <strong>Beállítások → Felhasználók</strong> menüpontban — a meghívott user emailben kap
              egy egyszer használatos linket a jelszó beállításához.
            </span>
          </div>
        </form>
      </div>
    </div>
  )
}

function SetupWizard() {
  const auth = useAuth()
  const nav = useNavigate()
  const toast = useToast()
  const setup = useSetupInitialAdmin()

  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [companyLegalName, setCompanyLegalName] = useState('')
  const [companyTaxNumber, setCompanyTaxNumber] = useState('')
  const [companyCountryCode, setCompanyCountryCode] = useState('HU')
  const [companyCurrency, setCompanyCurrency] = useState('HUF')
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (password.length < 8) {
      setError('A jelszó legalább 8 karakter hosszú legyen')
      return
    }
    if (password !== confirmPassword) {
      setError('A két jelszó nem egyezik')
      return
    }
    try {
      const session = await setup.mutateAsync({
        fullName,
        email,
        password,
        companyLegalName,
        companyTaxNumber,
        companyCountryCode,
        companyCurrency,
      })
      auth.login(session)
      toast.show({
        tone: 'success',
        title: 'Adminisztrátor létrehozva',
        description: `${session.user.fullName} · ${session.memberships[0]?.legalName}`,
      })
      nav('/invoices', { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ismeretlen hiba')
    }
  }

  const canSubmit =
    fullName.trim() &&
    email.includes('@') &&
    password.length >= 8 &&
    password === confirmPassword &&
    companyLegalName.trim() &&
    companyTaxNumber.trim()

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <Hero>
        <ul className="space-y-2 text-sm text-brand-50">
          <li className="flex items-start gap-2">
            <UserCog className="h-4 w-4 mt-0.5" />
            <span>Te leszel az első adminisztrátor — minden további user-t te hozol létre.</span>
          </li>
          <li className="flex items-start gap-2">
            <Building2 className="h-4 w-4 mt-0.5" />
            <span>Az első cég adatait most beállítjuk — később bármennyit hozzáadhatsz.</span>
          </li>
          <li className="flex items-start gap-2">
            <ShieldCheck className="h-4 w-4 mt-0.5" />
            <span>Minden későbbi művelet auditálva — a Beállítások → Audit log mindent rögzít.</span>
          </li>
        </ul>
      </Hero>

      <div className="flex items-center justify-center p-6 sm:p-10 overflow-y-auto">
        <form onSubmit={onSubmit} className="w-full max-w-md space-y-5">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Üdvözlünk!</h1>
            <p className="mt-1 text-sm text-slate-500">
              Még nincs admin user beállítva. Add meg az alábbi adatokat — egyetlen lépésben létrehozzuk
              az adminisztrátor fiókodat és az első céget.
            </p>
          </div>

          <fieldset className="space-y-3">
            <legend className="text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-1.5">
              <UserCog className="h-3.5 w-3.5" /> Adminisztrátor
            </legend>
            <div>
              <Label htmlFor="setup-name">Teljes név</Label>
              <Input
                id="setup-name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Pl. Kovács Anna"
                autoFocus
                required
              />
            </div>
            <div>
              <Label htmlFor="setup-email">Email cím</Label>
              <Input
                id="setup-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="anna@adaptivo.hu"
                required
              />
              <FieldHint>Ezzel az email címmel fogsz bejelentkezni ezután.</FieldHint>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="setup-pass">Jelszó</Label>
                <Input
                  id="setup-pass"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={8}
                  required
                />
                <FieldHint>Legalább 8 karakter.</FieldHint>
              </div>
              <div>
                <Label htmlFor="setup-pass2">Jelszó újra</Label>
                <Input
                  id="setup-pass2"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
            </div>
          </fieldset>

          <fieldset className="space-y-3">
            <legend className="text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-1.5">
              <Building2 className="h-3.5 w-3.5" /> Első cég
            </legend>
            <div>
              <Label htmlFor="setup-co-name">Cég neve</Label>
              <Input
                id="setup-co-name"
                value={companyLegalName}
                onChange={(e) => setCompanyLegalName(e.target.value)}
                placeholder="Pl. Adaptivo Kft."
                required
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <Label htmlFor="setup-co-tax">Adószám</Label>
                <Input
                  id="setup-co-tax"
                  value={companyTaxNumber}
                  onChange={(e) => setCompanyTaxNumber(e.target.value)}
                  placeholder="12345678-2-42"
                  required
                />
                <FieldHint>HU: 8 számjegy + áfakód + megyekód</FieldHint>
              </div>
              <div>
                <Label htmlFor="setup-co-country">Ország</Label>
                <Input
                  id="setup-co-country"
                  value={companyCountryCode}
                  onChange={(e) => setCompanyCountryCode(e.target.value.toUpperCase())}
                  maxLength={2}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="setup-co-currency">Alap deviza</Label>
              <select
                id="setup-co-currency"
                value={companyCurrency}
                onChange={(e) => setCompanyCurrency(e.target.value)}
                className="block w-full rounded-md border border-slate-300 h-10 px-3 text-sm"
              >
                <option value="HUF">HUF (Magyar forint)</option>
                <option value="EUR">EUR (Euró)</option>
                <option value="USD">USD (US dollár)</option>
              </select>
            </div>
          </fieldset>

          {error && <FieldError>{error}</FieldError>}

          <Button
            type="submit"
            fullWidth
            loading={setup.isPending}
            disabled={!canSubmit}
            leftIcon={<ShieldCheck className="h-4 w-4" />}
          >
            Adminisztrátor és cég létrehozása
          </Button>

          <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800">
            <p>
              <strong>Ezzel az egyszeri setuppal</strong> a fenti emaillel és jelszóval lépsz be ezután.
              További usereket a Beállítások → Felhasználók-ban hozhatsz létre.
            </p>
          </div>
        </form>
      </div>
    </div>
  )
}
